import { useState, useEffect } from "react";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

export const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      quote: "The team completely restructured our application architecture and built a scalable system that now supports 5x more users without performance issues. Their structured approach and long-term support gave us confidence to grow aggressively.",
      author: "Ritik Sharma",
      role: "Founder & CEO",
      company: "Haron India",
      avatar: "RS",
      rating: 5,
    },
    {
      quote: "What stood out was their dedication. We had direct access to developers who truly understood our business model. The platform they built is clean, secure, and designed for long-term scalability.",
      author: "Michael Rodriguez",
      role: "Founder",
      company: "The Mark Hotel",
      avatar: "MR",
      rating: 5,
    },
    {
      quote: "From planning to deployment, everything was structured and transparent. Their architecture-first mindset helped us avoid costly rebuilds later. The 2 years of free support is something we haven't seen anywhere else.",
      author: "Emily Watson",
      role: "VP of Operations",
      company: "TRESSEME",
      avatar: "EW",
      rating: 5,
    },
    {
      quote: "They didn't just build an app — they built a scalable digital foundation for our business. The performance improvements and system reliability directly increased our operational efficiency.",
      author: "David Kim",
      role: "Head of Engineering",
      company: "Addspay",
      avatar: "DK",
      rating: 5,
    },
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary/10 to-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-6">
            <Quote className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Client Success Stories</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Trusted by
            <span className="gradient-text"> founders,</span>
            <br />loved by developers
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            See what our clients have to say about their experience working with us.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12 relative">
            {/* Quote Icon */}
            <div className="absolute top-6 left-6 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Quote className="w-6 h-6 text-primary" />
            </div>

            {/* Content */}
            <div className="pt-8">
              {/* Rating */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-xl md:text-2xl font-heading leading-relaxed mb-8 min-h-[120px]">
                "{testimonials[activeIndex].quote}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold text-lg">
                  {testimonials[activeIndex].avatar}
                </div>
                <div>
                  <div className="font-heading font-semibold text-lg">
                    {testimonials[activeIndex].author}
                  </div>
                  <div className="text-muted-foreground">
                    {testimonials[activeIndex].role} at {testimonials[activeIndex].company}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border/50">
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === activeIndex
                        ? "w-8 bg-primary"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    }`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={prevTestimonial}
                  className="rounded-full glass"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={nextTestimonial}
                  className="rounded-full glass"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
